import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';

const Login = ({ onLogin }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [isPlayerRegister, setIsPlayerRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSubmitted, setResetSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'agent'
  });
  const [inviteToken, setInviteToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isPlayerRegister) {
        const response = await api.post('/auth/player-register', {
          invite_token: inviteToken,
          password: formData.password,
          full_name: formData.full_name
        });
        const { access_token, user } = response.data;
        toast.success('Account created! Welcome to your portal.');
        onLogin(access_token, user);
      } else if (isRegister) {
        const endpoint = '/auth/register';
        const payload = formData;
        await api.post(endpoint, payload);
        toast.success('Registration successful! Please wait for admin approval.');
        setIsRegister(false);
      } else {
        const response = await api.post('/auth/login', { email: formData.email, password: formData.password });
        const { access_token, user } = response.data;
        toast.success('Login successful!');
        onLogin(access_token, user);
      }
    } catch (error) {
      if (error.response?.status === 200 && isRegister) {
        toast.success('Registration successful! Please wait for admin approval.');
        setIsRegister(false);
      } else {
        toast.error(error.response?.data?.detail || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (!resetEmail) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await api.post('/auth/request-password-reset', { email: resetEmail });
      setResetSubmitted(true);
      toast.success('Reset request submitted');
    } catch { toast.error('Failed to submit request'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#09090B] noise-texture relative">
        <div className="hero-glow"></div>
        <div className="w-full max-w-md relative z-10">
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-[0.02em] leading-none text-white mb-3">
              DC Sports & Talent
            </h1>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Players &nbsp;|&nbsp; Partnerships &nbsp;|&nbsp; Performance</p>
            <p className="text-lg text-zinc-300 italic">More Than Football</p>
          </div>

          <div className="glass-card p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-2">
                {isForgotPassword ? 'Reset Password' : isPlayerRegister ? 'Player Setup' : isRegister ? 'Create Account' : 'Welcome Back'}
              </h2>
              <p className="text-sm text-zinc-400">
                {isForgotPassword ? 'Enter your email and we\'ll notify the admin to reset your password' : isPlayerRegister ? 'Enter your invite code to set up your account' : isRegister ? 'Register to get started' : 'Sign in to your account'}
              </p>
            </div>

            {isForgotPassword ? (
              resetSubmitted ? (
                <div className="space-y-4" data-testid="reset-submitted">
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <p className="text-green-400 text-sm font-medium">Reset request submitted!</p>
                    <p className="text-zinc-400 text-sm mt-1">Your agency admin has been notified and will reset your password shortly. Please check back soon.</p>
                  </div>
                  <button type="button" onClick={() => { setIsForgotPassword(false); setResetSubmitted(false); setResetEmail(''); }}
                    className="w-full py-3 bg-white/10 text-white hover:bg-white/20 font-bold uppercase text-sm rounded-md transition-all" data-testid="back-to-login-btn">
                    Back to Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetRequest} className="space-y-4" data-testid="forgot-password-form">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Email Address</label>
                    <input type="email" required value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                      data-testid="reset-email-input" placeholder="Enter your email"
                      className="w-full px-4 py-3 bg-zinc-900/50 border border-white/10 rounded-md text-white placeholder:text-zinc-600 outline-none focus:border-white transition-all text-sm" />
                  </div>
                  <button type="submit" disabled={loading} data-testid="submit-reset-btn"
                    className="w-full py-3 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide text-sm rounded-md transition-all disabled:opacity-50">
                    {loading ? 'Submitting...' : 'Request Password Reset'}
                  </button>
                  <button type="button" onClick={() => { setIsForgotPassword(false); setResetEmail(''); }}
                    className="w-full py-2 text-zinc-400 hover:text-white text-sm transition-colors">
                    Back to Login
                  </button>
                </form>
              )
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isPlayerRegister ? (
                <>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Invite Code *
                    </label>
                    <input
                      type="text"
                      required
                      data-testid="invite-code-input"
                      value={inviteToken}
                      onChange={(e) => setInviteToken(e.target.value)}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="Paste your invite code"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Set Password *
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="Choose a secure password"
                    />
                  </div>
                </>
              ) : (
                <>
                  {isRegister && (
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="register-fullname-input"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="Enter your full name"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  data-testid="login-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  required
                  data-testid="login-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                  placeholder="Enter your password"
                />
              </div>

              {isRegister && (
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : isPlayerRegister ? 'Set Up Account' : isRegister ? 'Register' : 'Login'}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              {!isPlayerRegister ? (
                <>
                  <button
                    onClick={() => { setIsRegister(!isRegister); setIsPlayerRegister(false); }}
                    className="text-sm text-zinc-400 hover:text-white transition-colors duration-200 block w-full"
                  >
                    {isRegister ? 'Already have an account? Login' : "Don't have an account? Register"}
                  </button>
                  <button
                    onClick={() => { setIsPlayerRegister(true); setIsRegister(false); }}
                    data-testid="player-invite-link"
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200 block w-full"
                  >
                    Have an invite code? Set up Player Account
                  </button>
                  {!isRegister && (
                    <button
                      onClick={() => { setIsForgotPassword(true); setIsRegister(false); setIsPlayerRegister(false); }}
                      data-testid="forgot-password-link"
                      className="text-sm text-orange-400 hover:text-orange-300 transition-colors duration-200 block w-full"
                    >
                      Forgot your password?
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => { setIsPlayerRegister(false); setIsRegister(false); }}
                  className="text-sm text-zinc-400 hover:text-white transition-colors duration-200"
                >
                  Back to Login
                </button>
              )}
            </div>
            </>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:block flex-1 relative overflow-hidden bg-black">
        <img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_abc5115b-3aff-4d95-91e5-09cfb902f9df/artifacts/6lcbblme_WhatsApp%20Image%202026-09-11%20at%2010.21.05.jpeg"
          alt="DC Sports & Talent"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#09090B]/80"></div>
      </div>
    </div>
  );
};

export default Login;